import { useEffect } from 'react';
import { prezzoTesto } from '../utils/prezzo';
import { useTranslation, Trans } from 'react-i18next';
import InstallCTA from '../components/InstallCTA';
import BannerOfferta from '../components/BannerOfferta';
import CurrencySelector from '../components/CurrencySelector';
import InAppBrowserWarning from '../components/InAppBrowserWarning';
import { useCurrency } from '../hooks/useCurrency';
import { usePricing } from '../hooks/usePricing';
import { useAuth } from '../hooks/useAuth';
import { useStartCheckout2TB } from '../hooks/useStartCheckout2TB';
import HeroCipherBackground from '../components/HeroCipherBackground';
import SiteNav from '../components/SiteNav';
import { useTema } from '../hooks/useTema';

// L'IMMAGINE DEL PRODOTTO NELL'EROE (2026-09-12).
// Otto file: telefono e finestra, ciascuno webp (sorgente principale) e png
// (riserva), a una e due volte la densita'. Importati e non messi in
// public/: cosi' passano dal build, prendono un nome con l'impronta del
// contenuto e possono essere messi in cache per sempre senza rischiare che
// una versione vecchia resti appesa in un proxy.
import vaultTelWebp from '../assets/vault/vault-telefono.webp';
import vaultTelWebp2 from '../assets/vault/vault-telefono@2x.webp';
import vaultTelPng from '../assets/vault/vault-telefono.png';
import vaultTelPng2 from '../assets/vault/vault-telefono@2x.png';
import vaultDeskWebp from '../assets/vault/vault-desktop.webp';
import vaultDeskWebp2 from '../assets/vault/vault-desktop@2x.webp';
import vaultDeskPng from '../assets/vault/vault-desktop.png';
import vaultDeskPng2 from '../assets/vault/vault-desktop@2x.png';
import { giornoDellaScadenza, giornoDopoLaScadenza } from '../utils/dataOfferta';
import { limiteLeggibile } from '../utils/limiti';

// Ripreso da 848c78b insieme al modulo email della hero.

export default function HomePage({ onLogin, onRegister, onRegisterBuyIntent, onAbout, onNews, onTerms, onPrivacy, onApriGuida}) {
  const { t, i18n } = useTranslation(['home', 'plans']);

  // Tema chiaro/scuro: definizione unica in hooks/useTema.js, usata anche da
  // Chi siamo e Novita' da quando la nav (e il selettore sole/luna) e' condivisa.
  const { tema, cambiaTema } = useTema();
  const { currency, setCurrency } = useCurrency();
  const { isAuthenticated } = useAuth();
  const { priceOf, founderPricingActive: isLaunch, daysLeft, prezzo2tbDopo, offerta1tbAttiva, offerta1tbFine, offerta2tbFine, limiti, posti5tb, posti } = usePricing(currency);
  // La data viene da OFFERTA_1TB_FINE (server), non e' scritta a mano qui.
  const scadenza1tb = giornoDellaScadenza(offerta1tbFine, i18n.language);
  // Il limite per file dei piani a pagamento, dal server: stesso numero per
  // 1, 2 e 5 TB (plans.js), quindi basta leggerlo una volta.
  const limitePagato = limiteLeggibile(limiti?.lifetime_2tb?.maxFileSize, i18n.language);
  // Il limite per file del piano GRATUITO (50 MB) viene dal server, dalla
  // stessa fonte dei piani a pagamento — mai un numero a mano. Il Free ha un
  // limite suo: "5 GB per file" (quello dei piani a pagamento) qui sarebbe
  // assurdo con 1 GB di spazio totale.
  const limiteFree = limiteLeggibile(limiti?.free?.maxFileSize, i18n.language);
  // Stessa cosa per l'offerta 2 TB: la fascia diceva "fino al 10 settembre"
  // mentre il server la teneva aperta fino al 30. Adesso legge OFFERTA_2TB_FINE.
  const scadenza2tb = giornoDellaScadenza(offerta2tbFine, i18n.language);
  const dopo2tb = giornoDopoLaScadenza(offerta2tbFine, i18n.language);
  const price1tb = priceOf('lifetime_1tb', currency);
  const price2tb = priceOf('lifetime_2tb', currency);
  // Il prezzo a cui si torna l'11 settembre. Tabella unica lato server
  // (DISPLAY_PRICES): mai scritto a mano qui ne' nelle traduzioni.
  const prezzo2tbDopoOfferta = prezzo2tbDopo;
  // 2026-09-01 — the public site shows ONE 2TB price to everyone: the full
  // one. No discount is read here any more, for anonymous OR logged-in
  // visitors. The welcome discount still exists, but it is now a private,
  // per-user thing that appears only inside the file manager (see
  // VaultPromoBanner) — never on the marketing pages, never in the hero
  // button, never as a struck-through "today only" number.
  //
  // Consequence, accepted deliberately: a user whose 48h window is still
  // open sees the full price here and is charged the discounted one at
  // checkout (the Stripe coupon is applied server-side and is unaffected by
  // this file). They pay less than they were shown, which is the safe
  // direction for that mismatch to point.
  const price2tbNow = price2tb ? prezzoTesto(price2tb.symbol, price2tb.amount, i18n.language) : '';
  const heroPriceSymbol = price2tb?.symbol;
  const heroPriceAmount = price2tb?.amount;
  const hero1tbSymbol = price1tb?.symbol;
  const hero1tbAmount = price1tb?.amount;
  // Prezzo pieno del 2TB, per la riga "dall'11 settembre, 99". Non scritto
  // nella traduzione: viene dalla stessa tabella che decide cosa si paga.
  const hero2tbDopo = prezzo2tbDopoOfferta;

  // Hero "buy 2TB" button (2026-08-28, corrected same day — the first
  // version always sent the visitor through the free/upgrade choice
  // screen, re-litigating a decision they'd already made and the fastest
  // way to lose an already-won sale). Two paths:
  //  - logged out (the only case reachable today — App.js only ever
  //    mounts HomePage inside its `if (!isAuthenticated)` branch, so
  //    isAuthenticated is structurally always false here — this check is
  //    a deliberate defensive guard in case that routing ever changes,
  //    not dead code covering a real path): goes to /register carrying a
  //    'hero_buy' intent (App.js's goToRegister/AuthPage's
  //    initialBuyIntent). App.js's page==='post-register' branch reads
  //    that intent and, on success, skips straight to AutoCheckoutPage —
  //    no free-vs-upgrade screen — instead of PostRegisterUpsellPage.
  //  - already authenticated: no registration needed, go straight to
  //    checkout via the same hook the post-registration auto-checkout
  //    page uses (in-app-browser warning included).
  // PROVA DEL 17/09/2026: il bottone dell'eroe propone il piano MINORE (1 TB a
  // 49) invece del 2 TB a 79, per vedere se l'offerta piu' bassa converte di
  // piu'. Una costante sola decide tutto — testo, prezzo, piano del checkout,
  // campagna nel registro (hero_buy_1tb / hero_1tb, cosi' il confronto con
  // hero_buy / hero_2tb resta leggibile). Per tornare indietro: false.
  const PROVA_EROE_1TB = false;
  const eroePiano = PROVA_EROE_1TB ? 'lifetime_1tb' : 'lifetime_2tb';
  const { buy2tb: heroBuy2tb, loading: heroBuyLoading, error: heroBuyError, inAppWarningProps: heroInAppWarningProps } = useStartCheckout2TB(PROVA_EROE_1TB ? 'hero_buy_1tb' : 'hero_buy', eroePiano);
  // 1TB a 59: da oggi e' l'offerta PRINCIPALE della hero, perche' gli annunci
  // attivi promettono esattamente quella (campagna italia_1tb_59). Chi clicca
  // un annuncio da 59 deve ritrovare 59, e ritrovarlo per primo.
  // L'intento viaggia fino alla sessione Stripe: campagna hero_buy_1tb.
  function handleHeroBuy1TB() {
    onRegisterBuyIntent({ plan: 'lifetime_1tb', campaign: 'hero_buy_1tb' });
  }

  function handleHeroBuy2TB() {
    if (isAuthenticated) { heroBuy2tb(); return; }
    onRegisterBuyIntent({ plan: eroePiano, campaign: PROVA_EROE_1TB ? 'hero_buy_1tb' : 'hero_buy' }, PROVA_EROE_1TB ? 'hero_1tb' : 'hero_2tb');
  }

  // Deep-link support: scroll to a section when arriving with a #hash (e.g. from the
  // About page), or when arriving straight at /plans — that URL is just the homepage
  // with the pricing section built in (see id="plans" below), not a separate page.
  useEffect(() => {
    const hash = window.location.hash?.slice(1);
    const target = hash || (window.location.pathname === '/plans' ? 'plans' : null);
    if (target) {
      setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  }, []);

  // IL BOTTONE DELLA FASCIA RESTA SEMPRE (2026-09-12).
  //
  // Qui c'era un IntersectionObserver che toglieva il bottone dalla fascia
  // quando quello dell'eroe entrava nello schermo. Sulla carta era "un
  // invito solo per schermata"; nell'uso vero il bottone spariva MENTRE si
  // scorreva, e un comando che sparisce da solo si legge come un difetto,
  // non come una scelta. Tolto del tutto: su telefono la fascia tiene il
  // suo bottone sempre, sul desktop non ce l'ha mai (App.css).
  //
    // I POSTI SOPRA OGNI CARD (2026-09-07). Un solo pezzo per tutti e tre i
  // piani: prima il numero grande esisteva solo per il 5 TB, e copiarlo tre
  // volte avrebbe prodotto tre varianti destinate a divergere.
  // Se il server non manda i posti per quel piano, non si disegna niente: il
  // piano semplicemente non e' a numero chiuso.
  function postiDi(pianoId) {
    const p = posti?.[pianoId];
    if (!p) return null;
    return (
      <div className={`cps-posti-grande${p.rimasti === 0 ? ' esauriti' : ''}`}>
        <span className="cps-posti-numero">{p.rimasti}</span>
        <span className="cps-posti-parola">
          {p.rimasti === 0 ? t('posti_esauriti')
            : p.rimasti === 1 ? t('piani_5tb_posto_uno') : t('piani_5tb_posti_disponibili')}
        </span>
      </div>
    );
  }

  function scrollTo(id) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }

  return (
    <div className="cps-root">
      {/* OFFERTA LAMPO (2026-09-07, test di qualche giorno). Due forme, un
          componente e un timer solo: sopra i 700px la barra a tutta larghezza
          fissa sopra la nav, sotto la card in basso dov'era gia'. Quale delle
          due si vede lo decide il CSS, non il JavaScript: entrambe sono nel
          documento e il media query ne nasconde una. */}
      <div className="ltc-banner-desktop"><BannerOfferta posto="barra" onRegisterBuyIntent={onRegisterBuyIntent} /></div>
      <div className="ltc-banner-mobile"><BannerOfferta posto="card" onRegisterBuyIntent={onRegisterBuyIntent} /></div>

      {/* Clouds */}
      <div className="cloud-layer" aria-hidden="true">
        <div className="cloud c1"/><div className="cloud c2"/>
        <div className="cloud c3"/><div className="cloud c4"/>
        <div className="cloud c5"/><div className="cloud c6"/>
        <div className="cloud c7"/><div className="cloud c8"/>
        <div className="cloud c9"/><div className="cloud c10"/>
      </div>

      {/* NAV */}
      {/* Barra unica per tutte le pagine pubbliche (components/SiteNav.jsx).
          Era copiata qui, in AboutPage e in NewsPage, e le tre copie erano gia'
          divergenti. `pagina="home"` mantiene la classe cps-nav-home, che in
          App.css nasconde "Registrati" sopra i 701px solo su questa pagina. */}
      <SiteNav
        pagina="home"
        onVaiA={scrollTo}
        onAbout={onAbout}
        onNews={onNews}
        onLogin={onLogin}
        onRegister={onRegister}
        tema={tema}
        onCambiaTema={cambiaTema}
      />

      {/* HERO */}
      <section id="hero" className={`cps-hero${isLaunch ? ' cps-hero-launch' : ''}`}>
          <HeroCipherBackground tema={tema} />
        <div className="cps-hero-row">
          {/* Split into top/bottom (instead of one cps-hero-content block) so the
              grid areas below can slot cps-hero-signup between them on mobile —
              right after the headline/sub, before the secondary CTA/trust/stats —
              instead of pushing it below all of that, off the first screen. */}
          <div className="cps-hero-top">
            {/* Badge dropped (2026-08-29) — it repeated the headline
                word-for-word two lines below and cost the page's most
                valuable pixels for zero new information. Title down to two
                lines, single color — was three lines each in a different
                green treatment, reading as five near-duplicate statements
                of the same "pay once" promise before the visitor ever sees
                pricing.
                Copy itself rewritten the same day: "Il tuo cloud. Per
                sempre." read as a promise the SERVICE lasts forever, which
                the Terms explicitly don't guarantee — only the payment is
                one-time. Now "Il tuo cloud a vita." / "Un solo pagamento."
                puts "a vita"/"for life" on the PAYMENT, not the service.
                hero_title_3 stays unused (see home.json) — its old wording
                is now hero_title_2's job. */}
            {/* PILLOLA: tolta il 2026-09-12 (ripeteva il titolo e su desktop,
                con l'eroe a due colonne, non si allineava alla colonna
                sinistra), RIMESSA il 2026-09-16 solo sul telefono: su desktop
                resta display:none (App.css, blocco min-width:1024px). Stesso
                elemento e stesso CSS .cps-hero-pill di prima, chiave hero_pill
                in home.json. */}
            <div className="cps-hero-pill">{t('hero_pill')}</div>
            {/* Titolo a tre righe, versione pre-c8db042 (recuperato da c0f8046):
                riga 1 nel colore base (crema in scuro), righe 2-3 nel verde del
                tema — .cps-hero-green, il gradiente storico gia' presente in
                App.css, non un verde nuovo. */}
            <h1 className="cps-hero-title">
              <span className="cps-hero-title-line">{t('hero_title_1')}</span>
              <span className="cps-hero-title-line"><span className="cps-hero-green">{t('hero_title_2')}</span></span>
              <span className="cps-hero-title-line"><span className="cps-hero-green">{t('hero_title_3')}</span></span>
            </h1>
            {/* hero_sub_line1/line2/forever dropped — redundant with this
                line, kept only here now. Keys still exist in home.json
                (used to be a second paragraph) in case they're wanted back.
                Rewritten 2026-08-29 to say what the product actually does
                (upload from any device, get it back from any device) instead
                of leading with "end-to-end encrypted zero-knowledge storage"
                — three English technical terms in a row, meaningless to
                someone who just clicked an ad and doesn't know what a cloud
                is yet. The privacy promise ("not even we can open them")
                stays, bolded.
                Split into two <p> elements (2026-08-29) — a single
                paragraph with an inline <strong> mid-sentence let the bold
                run start wherever the line happened to wrap on narrow
                phones ("...any device. Encrypted on your / phone..."),
                which read badly. Two separate block elements always start
                the second sentence on its own new line regardless of
                width. Second line also sized down a step from the first on
                mobile (see .cps-hero-product-line-emphasis) so four lines
                of bold body text can't outweigh the actual headline. */}
            {/* Sottotitolo su UNA riga (versione pre-2da4f1a): due paragrafi
                spingevano il pulsante oltre meta' schermo a 390px. */}
            {/* IL SOTTOTITOLO SI ACCORCIA DOVE LO SPAZIO COSTA (2026-09-12).
                Sotto i 1024px la prima schermata deve contenere titolo,
                sottotitolo, immagine e bottone: la seconda frase — "sui
                nostri dischi sono blocchi illeggibili" — e' la spiegazione,
                non la promessa, e scende sotto la card. Sul desktop, dove
                lo spazio c'e', resta dov'era. */}
            {/* IL SOTTOTITOLO DI AGOSTO (2026-09-18): «Nessun abbonamento. Nessun
                rinnovo. Paga una volta e il tuo spazio cifrato e' tuo per sempre.»,
                dalle chiavi hero_sub_line1/line2/forever gia' presenti in cinque
                lingue, con «per sempre» in verde grassetto. Stesso testo su
                computer (cps-sub-intera) e telefono (cps-sub-corta): la meta'
                che scendeva sotto la card (hero_sub_resto) non ha piu' motivo. */}
            <p className="cps-hero-product-line cps-sub-intera">{t('hero_sub_line1')} {t('hero_sub_line2')} <strong className="cps-hero-green">{t('hero_sub_forever')}</strong>.</p>
            <p className="cps-hero-product-line cps-sub-corta">{t('hero_sub_line1')} {t('hero_sub_line2')} <strong className="cps-hero-green">{t('hero_sub_forever')}</strong>.</p>
          </div>

            {/* IL PRODOTTO PRIMA DELLE PAROLE (2026-09-12). Chi arriva da un
                annuncio non sa cosa sia un cloud cifrato: vedere il gestore
                file col lucchetto su ogni riga spiega in un secondo quello
                che tre righe di titolo spiegano peggio.
                Sul telefono la versione telefono, sul desktop quella con la
                finestra: non e' la stessa immagine rimpicciolita, perche' a
                390px una schermata di desktop diventa illeggibile.
                width e height stanno su OGNI sorgente e non solo sull'img:
                le due varianti hanno proporzioni diverse (700x1388 contro
                1600x1012) e senza misure per ciascuna il browser riserverebbe
                lo spazio sbagliato e la pagina salterebbe al caricamento. */}
            <div className="cps-hero-vault">
              <picture>
                <source media="(max-width: 1023px)" type="image/webp"
                        srcSet={`${vaultTelWebp} 1x, ${vaultTelWebp2} 2x`} width="700" height="1388" />
                <source media="(max-width: 1023px)" type="image/png"
                        srcSet={`${vaultTelPng} 1x, ${vaultTelPng2} 2x`} width="700" height="1388" />
                <source type="image/webp"
                        srcSet={`${vaultDeskWebp} 1x, ${vaultDeskWebp2} 2x`} width="1600" height="1012" />
                <img
                  src={vaultDeskPng}
                  srcSet={`${vaultDeskPng} 1x, ${vaultDeskPng2} 2x`}
                  width="1600" height="1012"
                  loading="lazy" decoding="async"
                  alt={t('hero_vault_alt')}
                />
              </picture>
              {/* Quattro segnaposto-file che galleggiano ai bordi della
                  finestra (2026-09-15). Decorazione pura: aria-hidden, non
                  entrano nella lettura, non intercettano il click
                  (pointer-events:none in CSS), colori dai token del tema.
                  SVG generici scritti a mano — nessun marchio, nessun font
                  di icone, nessuna immagine esterna. Posizioni e animazione
                  in App.css (.cps-hero-fx*), ferme sotto prefers-reduced-motion. */}
              <span className="cps-hero-fx cps-hero-fx-1" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M9 17.5V6l9-2v9.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  <ellipse cx="6.5" cy="17.5" rx="2.5" ry="2" fill="currentColor"/>
                  <ellipse cx="15.5" cy="13.5" rx="2.5" ry="2" fill="currentColor"/>
                </svg>
              </span>
              <span className="cps-hero-fx cps-hero-fx-2" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M6 3h8l4 4v14H6z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                  <path d="M14 3v4h4" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                  <text x="12" y="17.6" textAnchor="middle" fontSize="5.4" fontWeight="700" fontFamily="sans-serif" fill="currentColor">PDF</text>
                </svg>
              </span>
              <span className="cps-hero-fx cps-hero-fx-3" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="6" width="18" height="12" rx="2.5" stroke="currentColor" strokeWidth="1.6"/>
                  <path d="M10 9.4l4.6 2.6L10 14.6z" fill="currentColor"/>
                </svg>
              </span>
              <span className="cps-hero-fx cps-hero-fx-4" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M6 3h8l4 4v14H6z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                  <path d="M14 3v4h4" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                  <path d="M9 12h6M9 15h6M9 18h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
              </span>
            </div>

          <div className="cps-hero-signup">
            {/* Due azioni (2026-09-05, allineato al pre-c8db042): il pulsante
                pieno verde e' il 2TB a 79, sotto il bottone bordato "Inizia
                gratis con 1GB" (register_entry hero_2tb / hero_free). Il
                modulo email a scomparsa del 2026-09-03 e' stato rimosso: il
                gratis torna a portare dritto a /register. */}
            {/* LA CARD COMINCIA DAL BOTTONE (2026-09-12). La riga del prezzo
                sopra il pulsante e' stata tolta: il prezzo torna DENTRO
                l'azione, "Compra 2 TB a vita — 79", su una riga sola. Un
                elemento in meno fra chi legge e chi decide. Sotto restano
                garanzia, "Inizia gratis con 1 GB", la sua nota e "Hai gia' un
                account?". */}
            {/* LA GARANZIA SALE SOPRA IL BOTTONE (2026-09-12). Stava fra i due
                pulsanti e, letta li', sembrava riferita a "Inizia gratis con
                1 GB" — cioe' prometteva il rimborso di una cosa che non si
                paga. Sopra il pulsante verde dice quello che deve dire:
                questo acquisto e' senza rischio.
                Piu' vicina al bottone d'acquisto (6px) che il bottone al
                gratuito (vedi App.css): la distanza e' quello che dice a
                colpo d'occhio quali due elementi stanno insieme. */}

            {heroPriceAmount != null && (
              <>
                <button
                  type="button"
                  className="cps-hero-primary-1tb"
                  onClick={handleHeroBuy2TB}
                  disabled={heroBuyLoading}
                >
                  <span className="cps-hero-primary-1tb-uno">
                    {PROVA_EROE_1TB
                      ? t('hero_primary_1tb_uno', { symbol: hero1tbSymbol, price: hero1tbAmount })
                      /* IL BARRATO NON STA PIU' QUI (2026-09-19). Il listino
                         precedente lo dice gia' la striscia fissa in fondo, e
                         sullo stesso schermo lo stesso prezzo barrato non puo'
                         comparire due volte: dentro l'azione resta solo quello
                         che si paga. La chiave e' quella "piena", cioe' le
                         stesse identiche parole senza la parte barrata. */
                      : t('hero_primary_2tb_uno_pieno', { symbol: heroPriceSymbol, price: heroPriceAmount })}
                  </span>
                </button>
                {/* LA GARANZIA SCENDE SOTTO IL PULSANTE (2026-09-19). Sopra
                    faceva da cappello e si prendeva la prima occhiata; qui e'
                    quello che e': una rassicurazione che si legge DOPO aver
                    visto il prezzo. Piccola, grigia, peso normale — non deve
                    competere con l'unica cosa forte della zona. L'emoji dello
                    scudo era un disegno di sistema, diverso su ogni telefono e
                    sempre colorato: al suo posto uno scudo vettoriale, piccolo,
                    nel verde del sito. */}
                <p className="cps-garanzia-sotto">
                  <svg className="cps-garanzia-sotto-icona" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
                    <path d="M8 1.4 3 3.3v4.2c0 3 2.1 5.8 5 7.1 2.9-1.3 5-4.1 5-7.1V3.3L8 1.4Z"
                          fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                    <path d="M5.9 8.1 7.4 9.6l2.9-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {t('garanzia_riga')}
                </p>
              </>
            )}

            {/* L'EROE DI SABATO 6 MATTINA, PRIMO TEMPO (2026-09-11).
                Dalle 14:00 del 6 settembre (3ac0fa0) il tasso di
                registrazione dei visitatori da Facebook e' passato da 9-11%
                a 2%, con venti ore a zero la domenica. Si torna all'ordine
                di sabato mattina (48214b0), UN cambio per volta e con la sua
                misura — quello che e' mancato il 6 e il 7:
                  pulsante verde -> garanzia -> riga dell'offerta ->
                  pulsante gratuito -> la sua nota.
                La garanzia torna sotto il pulsante verde, con la riga di
                sabato ("Garanzia 14 giorni · Soddisfatti o rimborsati");
                la nota sotto il gratuito ("senza carta, potrai passare a 2 TB
                quando vuoi") era stata tolta da 3ac0fa0 e non e' mai
                tornata. Le tre garanzie, il riquadro in fondo, i posti e
                l'orologio NON si toccano in questo tempo. */}

            {/* La stessa frase della fascia in cima, ripetuta qui: a 390px la
                fascia scorre via, e chi arriva al bottone deve comunque sapere
                fino a quando vale e cosa succede dopo. Nessun barrato, nessun
                "sconto": data di fine e prezzo successivo. */}
            {hero2tbDopo != null && scadenza2tb && (
              <p className="cps-hero-offerta-nota">
                {t('offerta_fine_riga', { symbol: heroPriceSymbol, dopo: hero2tbDopo, data: scadenza2tb, dataDopo: dopo2tb })}
              </p>
            )}

            {/* IL PULSANTE DEL GRATIS NON STA PIU' QUI (2026-09-06). L'eroe
                fa una domanda sola. Il gratuito non sparisce dal sito: e'
                l'ultima cosa della sezione dei prezzi, dopo le garanzie —
                chi ci arriva ha gia' letto tutto quello che si paga.
                register_entry 'hero_free' resta vivo altrove, quindi lo
                storico della misura non si spezza. */}
            {/* Il pulsante gratuito, DOPO la riga dell'offerta come sabato
                mattina, e sotto la nota che 3ac0fa0 aveva tolto. Stessa
                classe di sempre (.cps-hero-secondary-btn), stessa entrata
                di misura (hero_free). */}
            {/* «oppure» STA FUORI DAL TASTO (2026-09-18). E' la parola che
                lega il tasto principale a questo: dentro l'etichetta
                sembrerebbe parte del nome dell'offerta, e l'etichetta del
                tasto non cambia di una lettera. Fuori, piu' piccola e piu'
                tenue, si legge per quello che e': la seconda meta' di una
                frase. La riga non va a capo, quindi la parola non resta mai
                sola sopra il tasto. */}
            <p className="cps-hero-oppure-riga">
              <span className="cps-hero-oppure">{t('eroe_oppure')}</span>
              <button
                type="button"
                className="cps-hero-secondary-btn"
                onClick={() => onRegister && onRegister('hero_free')}
              >
                {t('eroe_gratis')}
              </button>
            </p>
            {/* La nota sotto il gratuito («senza carta di credito») e' stata tolta
                il 2026-09-18: l'elemento non c'e' piu', cosi' non resta nemmeno
                lo spazio che occupava. La chiave hero_secondary_note e' stata
                tolta da tutte e cinque le lingue. */}

            {heroBuyError && <div className="auth-error">⚠ {heroBuyError}</div>}
          </div>

          <div className="cps-hero-bottom">
            {/* La seconda meta' del sottotitolo, che sotto i 1024px non sta
                nella prima schermata. Qui la legge chi ha gia' visto il
                prodotto e il prezzo. */}
            {/* hero_sub_resto non si mostra piu' (2026-09-18): il sottotitolo
                intero sta nella hero, vedi sopra. */}
            {/* The two scroll-to buttons that used to live here (plans /
                how-it-works) are gone — the hero now has exactly one CTA,
                the email signup box. Both destinations are still reachable
                from the nav and further down the page (#plans, #how-it-works).
                Trust-line text dropped (2026-08-29) — it restated the three
                stat badges below in sentence form right above them ("EU ·
                GDPR · 14-day refund" vs. the same three facts as badges);
                the badges read faster, so they're the one copy that stays.
                Third badge changed from "∞ years of access" (vague — access
                to what, forever how) to the actual sales argument, one
                payment, no subscription. */}
            <div className="cps-hero-stats">
              <div className="cps-stat"><span>{t('hero_stat_eu_value')}</span><small>{t('hero_stat_eu_label')}</small></div>
              <div className="cps-stat-div"/>
              <div className="cps-stat"><span>AES-256</span><small>{t('hero_stat_aes_label')}</small></div>
              <div className="cps-stat-div"/>
              <div className="cps-stat"><span>∞</span><small>{t('hero_stat_infinity_label')}</small></div>
            </div>
          </div>
        </div>
      </section>

      {/* PLANS */}
      {/* HOW IT WORKS */}
      <section id="how-it-works" className="cps-section">
        <div className="cps-section-inner">
          <div className="cps-section-label">{t('how_it_works_label')}</div>
          <h2 className="cps-section-title">{t('how_it_works_title')}</h2>
          <div className="cps-steps">
            <div className="cps-step">
              <div className="cps-step-num">01</div>
              <h3>{t('steps_1_title')}</h3>
              <p>{t('steps_1_body')}</p>
            </div>
            <div className="cps-step-arrow">→</div>
            <div className="cps-step">
              <div className="cps-step-num">02</div>
              <h3>{t('steps_2_title')}</h3>
              <p>{t('steps_2_body')}</p>
            </div>
            <div className="cps-step-arrow">→</div>
            <div className="cps-step">
              <div className="cps-step-num">03</div>
              <h3>{t('steps_3_title')}</h3>
              <p>{t('steps_3_body')}</p>
            </div>
          </div>
        </div>
      </section>

      <section id="plans" className="cps-section">
        <div className="cps-section-inner">
          <div className="cps-section-label">{t('plans_label')}</div>
          <h2 className="cps-section-title">{t('plans_title')}</h2>
          <p className="cps-section-sub">{t('plans_sub', { currency })}</p>
          <CurrencySelector currency={currency} onChange={setCurrency} style={{marginBottom:28}} />
          <div className="cps-plans">
            {/* La card FREE non sta piu' fra i piani (2026-09-06): il
                gratuito e' l'ultima cosa della sezione, dopo le garanzie.
                Metterlo in mezzo ai piani a pagamento significava offrirlo
                come alternativa alla pari, prima ancora che uno avesse
                letto perche' vale la pena pagare. register_entry
                'card_free' resta vivo laggiu', quindi la misura continua. */}
            {/* 2TB — recommended: center on desktop (see .cps-plans order),
                first after Free on mobile (same DOM order, stacked) */}
            <div className="cps-plan cps-plan-popular">
              <div className="cps-popular-badge">✨ {t('plans_popular_badge')}</div>
              {postiDi('lifetime_2tb')}
              <div className="cps-plan-header">
                <h3>{t('plans_2tb_name')}</h3>
              </div>
              {/* One price, no struck-through "today only" row — see the
                  comment next to price2tbNow above (2026-09-01). */}
              <div className="cps-plan-price">
                <div className="cps-price-row">
                  {/* Il 99 barrato torna accanto al 79 (2026-09-11), come nel
                      banner: stesso numero del server (lifetime_2tb_dopo),
                      stessa famiglia di caratteri del 79, un gradino sotto. */}
                  {hero2tbDopo != null && (
                    <s className="cps-price-struck">{prezzoTesto(price2tb?.symbol, hero2tbDopo, i18n.language)}</s>
                  )}
                  <span className="cps-price-amount">{prezzoTesto(price2tb?.symbol, price2tb?.amount, i18n.language)}</span>
                </div>
                {/* Stessa formula della fascia e della hero: data di fine e
                    prezzo successivo, mai un barrato. */}
                {hero2tbDopo != null && scadenza2tb && (
                  <div className="cps-plan-offerta">
                    <span>{t('offerta_fino_al', { data: scadenza2tb })}</span>
                    <span className="cps-plan-offerta-dopo">{t('offerta_dall_11', { symbol: price2tb?.symbol, dopo: hero2tbDopo, dataDopo: dopo2tb })}</span>
                  </div>
                )}
                <span className="cps-price-once">{t('plans_price_once')}</span>
              </div>
              <ul className="cps-plan-features">
                <li className="cps-feat-chiave">✅ {t('plans_feat_storage', { size: '2 TB' })}</li>
                <li>✅ {t('plans_feat_unlimited')}</li>
                <li className="cps-feat-chiave">✅ {t('plans_feat_encryption')}</li>
                <li>✅ {t('plans_feat_2fa')}</li>
                <li>✅ {t('plans_feat_webdav')}</li>
                <li>🕘 {t('plans_feat_versions')}</li>
                <li>✏ {t('plans_feat_editor')}</li>
                <li>👪 {t('plans_feat_family', { count: 5 })}</li>
                <li className="cps-feat-chiave">✅ {t('plans_feat_lifetime')}</li>
                <li>✅ {t('plans_feat_vat')}</li>
                {limitePagato && <li>✅ {t('plans_feat_file_size', { limite: limitePagato })}</li>}
                <li className="cps-feat-altro">{t('plans_feat_altro')}</li>
              </ul>
              <button className="cps-plan-btn cps-plan-btn-popular" onClick={() => onRegisterBuyIntent({ plan: 'lifetime_2tb', campaign: 'plans_2tb' })}>{t('plans_2tb_buy')}</button>
              <p className="cps-garanzia-riga cps-garanzia-card"><span aria-hidden="true">🛡️</span>{t('garanzia_riga')}</p>
            </div>

            {/* 5 TB — STESSA CARD DEL 2 TB, con sopra i posti (2026-09-07).
                Non e' piu' una card speciale con un suo stile: usa le stesse
                classi delle altre, cosi' le tre si leggono come una famiglia e
                la differenza sta in cio' che dicono, non in come sono fatte.
                L'unica aggiunta e' il numero dei posti, grande, sopra il
                nome: e' l'informazione che fa decidere in fretta, e viene dal
                server — quando finiscono, la card lo dice da sola. */}
            {posti5tb?.aperti && priceOf('lifetime_5tb', currency)?.amount != null && (
              <div className="cps-plan cps-plan-5tb">
                {postiDi('lifetime_5tb')}
                <div className="cps-plan-header">
                  <h3>{t('piani_5tb_nome')}</h3>
                </div>
                <div className="cps-plan-price">
                  <span className="cps-price-amount">{prezzoTesto(priceOf('lifetime_5tb', currency).symbol, priceOf('lifetime_5tb', currency).amount, i18n.language)}</span>
                  <span className="cps-price-once">{t('plans_price_once')}</span>
                </div>
                <ul className="cps-plan-features">
                  <li className="cps-feat-chiave">✅ {t('plans_feat_storage', { size: '5 TB' })}</li>
                  <li>✅ {t('plans_feat_unlimited')}</li>
                  <li className="cps-feat-chiave">✅ {t('plans_feat_encryption')}</li>
                  <li>✅ {t('plans_feat_2fa')}</li>
                  <li>✅ {t('plans_feat_webdav')}</li>
                  <li>🕘 {t('plans_feat_versions')}</li>
                  <li>✏ {t('plans_feat_editor')}</li>
                  <li>👪 {t('plans_feat_family', { count: 5 })}</li>
                  <li className="cps-feat-chiave">✅ {t('plans_feat_lifetime')}</li>
                  <li>✅ {t('plans_feat_vat')}</li>
                  {limitePagato && <li>✅ {t('plans_feat_file_size', { limite: limitePagato })}</li>}
                  <li className="cps-feat-altro">{t('plans_feat_altro')}</li>
                </ul>
                <button className="cps-plan-btn" onClick={() => onRegisterBuyIntent({ plan: 'lifetime_5tb', campaign: 'plans_5tb' })}>{t('piani_5tb_cta')}</button>
                <p className="cps-garanzia-riga cps-garanzia-card"><span aria-hidden="true">🛡️</span>{t('garanzia_riga')}</p>
              </div>
            )}

            {/* 1TB */}
            <div className="cps-plan cps-plan-1tb">
              {isLaunch && <div className="cps-popular-badge" style={{background:'var(--giallo, #ca8a04)',top:-14}}>🎉 {t('plans_founder_badge')}</div>}
              {postiDi('lifetime_1tb')}
              <div className="cps-plan-header">
                <h3>{t('plans_1tb_name')}</h3>
              </div>
              <div className="cps-plan-price">
                <span className="cps-price-amount">{prezzoTesto(price1tb?.symbol, price1tb?.amount, i18n.language)}</span>
                <span className="cps-price-once">{t('plans_price_once')}</span>
                {/* "fino al 13 settembre" — solo finche' il server dice che
                    l'offerta 1TB e' attiva (offerta1tbFine nel pricing). Il
                    prezzo qui sopra e' gia' quello vero (49): niente barrato
                    e niente parola "sconto" — 49 era il prezzo fino all'1/9,
                    quindi il piu' basso degli ultimi 30 giorni e' 49 e un 59
                    barrato sarebbe un riferimento che non regge. Dopo il 13
                    la riga sparisce e il prezzo torna 59 da solo. */}
                {offerta1tbAttiva && scadenza1tb && <span className="cps-price-once">{t('plans_1tb_fino', { data: scadenza1tb })}</span>}
              </div>
              <ul className="cps-plan-features">
                <li className="cps-feat-chiave">✅ {t('plans_feat_storage', { size: '1 TB' })}</li>
                <li>✅ {t('plans_feat_unlimited')}</li>
                <li className="cps-feat-chiave">✅ {t('plans_feat_encryption')}</li>
                <li>✅ {t('plans_feat_2fa')}</li>
                <li>✅ {t('plans_feat_webdav')}</li>
                <li>🕘 {t('plans_feat_versions')}</li>
                <li>✏ {t('plans_feat_editor')}</li>
                <li>👪 {t('plans_feat_family', { count: 3 })}</li>
                <li className="cps-feat-chiave">✅ {t('plans_feat_lifetime')}</li>
                <li>✅ {t('plans_feat_vat')}</li>
                {limitePagato && <li>✅ {t('plans_feat_file_size', { limite: limitePagato })}</li>}
                <li className="cps-feat-altro">{t('plans_feat_altro')}</li>
              </ul>
              <button className="cps-plan-btn" onClick={() => onRegisterBuyIntent({ plan: 'lifetime_1tb', campaign: 'plans_1tb' })}>{t('plans_1tb_buy')}</button>
              <p className="cps-garanzia-riga cps-garanzia-card"><span aria-hidden="true">🛡️</span>{t('garanzia_riga')}</p>
            </div>

            {/* ── FREE, ULTIMA CARD (2026-09-11) ───────────────────────────
                La sezione mostrava tre card e chi guardava non vedeva da
                nessuna parte cosa si ha senza pagare. Il 2026-09-06 il
                gratuito era stato tolto dai piani per non offrirlo alla pari
                prima ancora di aver letto perche' vale la pena pagare: quel
                motivo resta, ed e' per questo che sta ULTIMA e non prima —
                dopo le tre a pagamento, su schermo largo e su telefono
                (l'ordine su telefono lo fissa .cps-plans .cps-plan-free in
                App.css, order:4).
                Le stringhe sono quelle della pagina dei piani (namespace
                plans), le stesse gia' usate li' e nel vault: se cambiano in
                un posto cambiano in tutti e tre. Nessun testo nuovo.
                Niente prezzo: al suo posto la parola "Gratis"/"Free", come
                sulla pagina dei piani. */}
            <div className="cps-plan cps-plan-free">
              <div className="cps-plan-header">
                <h3>{t('plans:plan_free_name')}</h3>
              </div>
              <div className="cps-plan-price">
                {/* Stessa classe e stesso corpo dei prezzi delle altre card
                    (.cps-price-amount: Fraunces 52px): la parola sta dove sta
                    la cifra, non un gradino sotto. */}
                <span className="cps-price-amount">{t('plans:price_free')}</span>
                <span className="cps-price-once">{t('plans:plan_free_desc')}</span>
              </div>
              <ul className="cps-plan-features">
                {/* Le prime cinque voci portano il segno di spunta come su
                    tutte le altre card; dalla sesta la stringa ha gia' il suo
                    simbolo (🕘 ✏ 👪), identica alla pagina dei piani. */}
                {[1,2,3,4,5].map(n => {
                  // feature_2 e' "Max {{limite}} per file": si interpola col
                  // limite del Free dal server. Finche' non e' arrivato, la
                  // riga non si mostra invece di lasciare il segnaposto grezzo.
                  const testo = t(`plans:plan_free_feature_${n}`, { limite: limiteFree || '' });
                  if (/Max\s+per file/.test(testo)) return null;
                  return <li key={n} className={n <= 3 ? 'cps-feat-chiave' : undefined}>✅ {testo}</li>;
                })}
                {[6,7,8].map(n => (
                  <li key={n}>{t(`plans:plan_free_feature_${n}`)}</li>
                ))}
              </ul>
              {/* Stesso ingresso di misura di prima ('card_free'), cosi' il
                  conteggio delle iscrizioni dal gratuito non si spezza.
                  Stessa classe e stesso posto del pulsante delle altre tre. */}
              <button type="button" className="cps-plan-btn" onClick={() => onRegister('card_free')}>
                {t('gratuito_cta')}
              </button>
              {/* Segnaposto invisibile: tiene il pulsante del gratuito alla
                  stessa altezza di quelli delle altre tre, che sotto hanno la
                  riga della garanzia. Non e' un testo nascosto da leggere —
                  aria-hidden lo toglie agli screen reader — e la garanzia dei
                  14 giorni resta scritta solo dove vale davvero. */}
              <p className="cps-garanzia-riga cps-garanzia-card cps-garanzia-segnaposto" aria-hidden="true">
                <span aria-hidden="true">🛡️</span>{t('garanzia_riga')}
              </p>
            </div>

          </div>

          {/* IL PERCHE' DEI POSTI, UNA VOLTA SOLA, SOTTO TUTTE E TRE LE CARD
              (2026-09-07). Prima era legato al solo 5 TB — titolo compreso —
              e ora che tutti i piani sono a numero chiuso spiegherebbe una
              regola sola su tre. Compare se ALMENO UN piano ha i posti:
              spiegare una scarsita' che non si vede sarebbe peggio che non
              spiegarla, ma basta un piano perche' la spiegazione serva. */}
          {posti && Object.keys(posti).length > 0 && (
            <div className="cps-perche-posti">
              <h3>{t('perche_titolo')}</h3>
              <p>{t('perche_testo')}</p>
            </div>
          )}

          {/* ── LE TRE GARANZIE (2026-09-06) ────────────────────────────────
              Prendono il posto del vecchio riquadro unico: dicevano una cosa
              sola dove ce ne sono tre da dire, e la piu' importante — che la
              chiave nasce sul dispositivo del cliente — non c'era. */}
          <div className="cps-garanzie">
            {[
              ['🛡️', 'garanzia_rimborso_t', 'garanzia_rimborso_c'],
              ['🔒', 'garanzia_chiave_t', 'garanzia_chiave_c'],
              ['🇮🇹', 'garanzia_italia_t', 'garanzia_italia_c'],
            ].map(([icona, titolo, corpo]) => (
              <div className="cps-garanzia" key={titolo}>
                <span className="cps-garanzia-icona" aria-hidden="true">{icona}</span>
                <div>
                  <h4>{t(titolo)}</h4>
                  <p>{t(corpo)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* ── IL GRATUITO, ULTIMO ─────────────────────────────────────────
              Dopo le garanzie, non fra i piani: chi arriva qui ha gia' letto
              tutto quello che si paga. Pulsante col solo bordo, nessun
              colore — c'e', ma non compete. */}
          <div className="cps-gratuito">
            <h3>{t('gratuito_titolo')}</h3>
            <p>{t('gratuito_riga')}</p>
            <button type="button" className="cps-btn-vuoto" onClick={() => onRegister('card_free')}>
              {t('gratuito_cta')}
            </button>
          </div>
        </div>
      </section>

      {/* NO SUBSCRIPTION */}
      <section className="cps-no-sub">
        <div className="cps-section-inner">
          <div className="cps-no-sub-grid">
            <div className="cps-no-sub-item bad">
              <div className="cps-no-sub-icon">❌</div>
              <h3>{t('nosub_bad_title')}</h3>
              <ul>
                <li>{t('nosub_bad_item1')}</li>
                <li>{t('nosub_bad_item2')}</li>
                <li>{t('nosub_bad_item3')}</li>
                <li>{t('nosub_bad_item4')}</li>
              </ul>
              <div className="cps-cost-calc">{t('nosub_over_10_years')} <strong>{i18n.language === 'de' ? '1.199,00\u00a0€' : '€1,199'}</strong></div>
            </div>
            <div className="cps-no-sub-vs">{t('nosub_vs')}</div>
            <div className="cps-no-sub-item good">
              <div className="cps-no-sub-icon">✅</div>
              <h3>{t('nosub_good_title')}</h3>
              <ul>
                <li>{t('nosub_good_item1', { price: price2tbNow })}</li>
                <li>{t('nosub_good_item2')}</li>
                <li>{t('nosub_good_item3')}</li>
                <li>{t('nosub_good_item4')}</li>
              </ul>
              <div className="cps-cost-calc">{t('nosub_over_10_years')} <strong>{price2tbNow}</strong></div>
            </div>
          </div>
        </div>
      </section>

      {/* SECURITY */}
      <section id="security" className="cps-section cps-section-dark">
        <div className="cps-section-inner">
          <div className="cps-section-label">{t('security_label')}</div>
          <h2 className="cps-section-title">{t('security_title_1')}<br/>{t('security_title_2')}</h2>
          <p style={{fontSize:17,color:'var(--text2)',lineHeight:1.7,maxWidth:640,marginBottom:48}}>
            {t('security_intro_1')} <strong style={{color:'var(--green)'}}>{t('security_intro_strong')}</strong> {t('security_intro_2')}
          </p>

          {/* Diagram */}
          <div className="cps-crypto-diagram">
            <div className="cps-crypto-side">
              <div className="cps-crypto-label">🖥️ {t('crypto_browser_label')}</div>
              <div className="cps-crypto-items">
                <div className="cps-crypto-item good">✓ {t('crypto_item_password')}</div>
                <div className="cps-crypto-item good">✓ {t('crypto_item_key')}</div>
                <div className="cps-crypto-item good">✓ {t('crypto_item_plaintext')}</div>
                <div className="cps-crypto-item good">✓ {t('crypto_item_process')}</div>
              </div>
            </div>
            <div className="cps-crypto-middle">
              <div className="cps-crypto-arrow">
                <span style={{fontSize:11,color:'var(--text3)',fontFamily:'JetBrains Mono,monospace'}}>{t('crypto_arrow_label')}</span>
                <div style={{width:1,height:30,background:'linear-gradient(to bottom,var(--border),var(--green))',margin:'4px auto'}}/>
              </div>
              <div style={{fontSize:28,filter:'drop-shadow(0 0 10px rgba(34,197,94,.4))'}}>🔐</div>
              <div className="cps-crypto-arrow">
                <div style={{width:1,height:30,background:'linear-gradient(to bottom,var(--green),var(--border))',margin:'4px auto'}}/>
                <span style={{fontSize:11,color:'var(--text3)',fontFamily:'JetBrains Mono,monospace'}}>{t('crypto_arrow_label')}</span>
              </div>
            </div>
            <div className="cps-crypto-side">
              <div className="cps-crypto-label">🖧 {t('crypto_server_label')}</div>
              <div className="cps-crypto-items">
                <div className="cps-crypto-item bad">✗ {t('crypto_item_password')}</div>
                <div className="cps-crypto-item bad">✗ {t('crypto_server_keys')}</div>
                <div className="cps-crypto-item bad">✗ {t('crypto_server_files')}</div>
                <div className="cps-crypto-item good">✓ {t('crypto_server_blobs')}</div>
              </div>
            </div>
          </div>

          {/* Tech cards */}
          <div className="cps-tech-cards">
            <div className="cps-tech-card">
              <div className="cps-tech-icon">🔒</div>
              <h4>{t('tech1_title')}</h4>
              <p>{t('tech1_body_1')} <strong>{t('tech1_body_strong')}</strong> {t('tech1_body_2')}</p>
            </div>
            <div className="cps-tech-card">
              <div className="cps-tech-icon">🔑</div>
              <h4>{t('tech2_title')}</h4>
              <p>{t('tech2_body_1')} <strong>{t('tech2_body_strong')}</strong>. {t('tech2_body_2')}</p>
            </div>
            <div className="cps-tech-card">
              <div className="cps-tech-icon">🎲</div>
              <h4>{t('tech3_title')}</h4>
              <p>{t('tech3_body')}</p>
            </div>
            <div className="cps-tech-card">
              <div className="cps-tech-icon">🌐</div>
              <h4>{t('tech4_title')}</h4>
              <p>{t('tech4_body_1')} <strong>{t('tech4_body_strong')}</strong> {t('tech4_body_2')}</p>
            </div>
          </div>

          {/* Compare */}
          <div style={{marginTop:48}}>
            <h3 style={{fontFamily:'Fraunces,serif',fontSize:26,fontWeight:700,marginBottom:24,textAlign:'center'}}>{t('compare_title')}</h3>
            <div className="cps-compare">
              <div className="cps-compare-box cps-compare-you">
                <div className="cps-compare-label">👤 {t('compare_you_label')}</div>
                <div className="cps-compare-row"><span>🖼️</span><div><strong>vacation-2024.jpg</strong><small>{t('compare_you_photo_desc')}</small></div></div>
                <div className="cps-compare-row"><span>📄</span><div><strong>contract.pdf</strong><small>{t('compare_you_doc_desc')}</small></div></div>
                <div className="cps-compare-row"><span>🎵</span><div><strong>song.mp3</strong><small>{t('compare_you_audio_desc')}</small></div></div>
              </div>
              <div className="cps-compare-vs">{t('compare_vs')}</div>
              <div className="cps-compare-box cps-compare-server">
                <div className="cps-compare-label">🖧 {t('compare_server_label')}</div>
                <div className="cps-compare-row"><span>🔒</span><div><strong>a3f9bc2e...d84f.bin</strong><small>{t('compare_server_desc')}</small></div></div>
                <div className="cps-compare-row"><span>🔒</span><div><strong>7c1d45ab...e923.bin</strong><small>{t('compare_server_desc')}</small></div></div>
                <div className="cps-compare-row"><span>🔒</span><div><strong>f2a87c3d...1b56.bin</strong><small>{t('compare_server_desc')}</small></div></div>
              </div>
            </div>
          </div>

          {/* Legal */}
          <div style={{marginTop:48}}>
            <div style={{background:'var(--verde-velo, rgba(34,197,94,.04))',border:'1px solid var(--verde-bordo, rgba(34,197,94,.15))',borderRadius:16,padding:32}}>
              <div style={{display:'flex',gap:16,alignItems:'flex-start',flexWrap:'wrap'}}>
                <div style={{fontSize:40,flexShrink:0}}>⚖️</div>
                <div style={{flex:1,minWidth:260}}>
                  <h3 style={{fontFamily:'Fraunces,serif',fontSize:24,fontWeight:700,marginBottom:12}}>
                    {t('legal_title')}
                  </h3>
                  <p style={{fontSize:15,color:'var(--text2)',lineHeight:1.8,marginBottom:16}}>
                    {t('legal_p1_1')} <strong style={{color:'var(--text)'}}>{t('legal_p1_strong')}</strong>. {t('legal_p1_2')}
                  </p>
                  <p style={{fontSize:15,color:'var(--text2)',lineHeight:1.8,marginBottom:20}}>
                    {t('legal_p2_1')} <strong style={{color:'var(--green)'}}>{t('legal_p2_strong')}</strong>. {t('legal_p2_2')}
                  </p>
                  <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
                    <div style={{background:'var(--bg)',border:'1px solid var(--border)',borderRadius:10,padding:'12px 16px',flex:1,minWidth:200}}>
                      <div style={{fontSize:11,color:'var(--text3)',fontFamily:'JetBrains Mono,monospace',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:6}}>{t('legal_could_handover_title')}</div>
                      <div style={{fontSize:13,color:'var(--rosso, #f87171)',fontFamily:'JetBrains Mono,monospace',lineHeight:1.8}}>
                        ✗ {t('legal_could_handover_1')}<br/>
                        ✗ {t('legal_could_handover_2')}<br/>
                        ✗ {t('legal_could_handover_3')}<br/>
                        ✓ {t('legal_could_handover_4')}
                      </div>
                    </div>
                    <div style={{background:'var(--bg)',border:'1px solid var(--verde-bordo, rgba(34,197,94,.2))',borderRadius:10,padding:'12px 16px',flex:1,minWidth:200}}>
                      <div style={{fontSize:11,color:'var(--text3)',fontFamily:'JetBrains Mono,monospace',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:6}}>{t('legal_server_sees_title')}</div>
                      {/* var(--green-text): il verde dei riempimenti come testo si ferma a
                          4,38:1 sul fondo chiaro. In linea, quindi non correggibile
                          dal CSS ambitato. */}
                      <div style={{fontSize:12,color:'var(--green-text, var(--green))',fontFamily:'JetBrains Mono,monospace',lineHeight:1.8,wordBreak:'break-all'}}>
                        3a9f2c8b4d1e7f6a...<br/>
                        c7d4e2f9a3b8c1d5...<br/>
                        9e1f4a7c2d5b8e3f...
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Data protection details */}
          <div style={{marginTop:48}}>
            <div style={{background:'var(--verde-velo, rgba(34,197,94,.04))',border:'1px solid var(--verde-bordo, rgba(34,197,94,.15))',borderRadius:16,padding:32}}>
              <h3 style={{fontFamily:'Fraunces,serif',fontSize:26,fontWeight:700,marginBottom:24,textAlign:'center'}}>{t('dp_title')}</h3>

              <div style={{marginBottom:24}}>
                <h4 style={{fontSize:16,fontWeight:700,color:'var(--text)',marginBottom:8}}>{t('dp_encrypted_title')}</h4>
                <p style={{fontSize:15,color:'var(--text2)',lineHeight:1.8}}>{t('dp_encrypted_body')}</p>
              </div>

              <div style={{marginBottom:24}}>
                <h4 style={{fontSize:16,fontWeight:700,color:'var(--text)',marginBottom:8}}>{t('dp_europe_title')}</h4>
                <p style={{fontSize:15,color:'var(--text2)',lineHeight:1.8}}>{t('dp_europe_body')}</p>
              </div>

              <div style={{marginBottom:24}}>
                <h4 style={{fontSize:16,fontWeight:700,color:'var(--text)',marginBottom:8}}>{t('dp_backup_title')}</h4>
                <p style={{fontSize:15,color:'var(--text2)',lineHeight:1.8,marginBottom:12}}>{t('dp_backup_intro')}</p>
                <ul style={{margin:'0 0 12px 20px',color:'var(--text2)',fontSize:15,lineHeight:1.9}}>
                  <li>{t('dp_backup_li1')}</li>
                  <li>{t('dp_backup_li2')}</li>
                </ul>
                <p style={{fontSize:15,color:'var(--text2)',lineHeight:1.8}}>{t('dp_backup_body')}</p>
              </div>

              <div>
                <h4 style={{fontSize:16,fontWeight:700,color:'var(--text)',marginBottom:8}}>{t('dp_recovery_title')}</h4>
                <p style={{fontSize:15,color:'var(--text2)',lineHeight:1.8}}>{t('dp_recovery_body')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROADMAP */}
      <section id="roadmap" className="cps-section">
        <div className="cps-section-inner">
          <div className="cps-section-label">{t('roadmap_label')}</div>
          <h2 className="cps-section-title">{t('roadmap_title')}</h2>
          <p className="cps-section-sub">{t('roadmap_sub')}</p>

          <div className="cps-roadmap-grid">
            <div className="cps-roadmap-card">
              <div className="cps-roadmap-icon">🕘</div>
              <h4>{t('roadmap_1_title')}</h4>
              <p>{t('roadmap_1_body')}</p>
              <span className="cps-roadmap-status active"><span className="dot"/>{t('roadmap_status_active')}</span>
            </div>
            <div className="cps-roadmap-card">
              <div className="cps-roadmap-icon">✏</div>
              <h4>{t('roadmap_2_title')}</h4>
              <p>{t('roadmap_2_body')}</p>
              <span className="cps-roadmap-status active"><span className="dot"/>{t('roadmap_status_active')}</span>
            </div>
            <div className="cps-roadmap-card">
              <div className="cps-roadmap-icon">👪</div>
              <h4>{t('roadmap_3_title')}</h4>
              <p>{t('roadmap_3_body')}</p>
              <span className="cps-roadmap-status active"><span className="dot"/>{t('roadmap_status_active')}</span>
            </div>
            <div className="cps-roadmap-card">
              <div className="cps-roadmap-icon">🎫</div>
              <h4>{t('roadmap_4_title')}</h4>
              <p>{t('roadmap_4_body')}</p>
              <span className="cps-roadmap-status active"><span className="dot"/>{t('roadmap_status_active')}</span>
            </div>
            <div className="cps-roadmap-card">
              <div className="cps-roadmap-icon">🔄</div>
              <h4>{t('roadmap_5_title')}</h4>
              <p>{t('roadmap_5_body')}</p>
              <span className="cps-roadmap-status active"><span className="dot"/>{t('roadmap_status_active')}</span>
            </div>
            <div className="cps-roadmap-card">
              <div className="cps-roadmap-icon">🌐</div>
              <h4>{t('roadmap_6_title')}</h4>
              <p>{t('roadmap_6_body')}</p>
              <span className="cps-roadmap-status active"><span className="dot"/>{t('roadmap_status_active')}</span>
            </div>
            <div className="cps-roadmap-card">
              <div className="cps-roadmap-icon">🍎</div>
              <h4>{t('roadmap_7_title')}</h4>
              <p>{t('roadmap_7_body')}</p>
              <span className="cps-roadmap-status dev"><span className="dot"/>{t('roadmap_status_dev')}</span>
            </div>
            <div className="cps-roadmap-card">
              <div className="cps-roadmap-icon">🪟</div>
              <h4>{t('roadmap_8_title')}</h4>
              <p>{t('roadmap_8_body')}</p>
              <span className="cps-roadmap-status dev"><span className="dot"/>{t('roadmap_status_dev')}</span>
            </div>
            <div className="cps-roadmap-card">
              <div className="cps-roadmap-icon">🤖</div>
              <h4>{t('roadmap_9_title')}</h4>
              <p>{t('roadmap_9_body')}</p>
              {/* VIA IL DOWNLOAD DELL'APK (2026-09-19). L'app e' in revisione
                  su Google Play: finche' non passa, dal sito non si scarica
                  piu' niente, e il riquadro dice dove sara' e da quando
                  invece di offrire un file. Il contrassegno non puo' restare
                  "Disponibile ora", che oggi sarebbe falso. I file .apk
                  restano sul disco per chi ha un link vecchio: non e' il
                  sito a portarceli. */}
              <span className="cps-roadmap-status dev"><span className="dot"/>{t('roadmap_status_revisione')}</span>
            </div>
            <div className="cps-roadmap-card">
              <div className="cps-roadmap-icon">📱</div>
              <h4>{t('roadmap_10_title')}</h4>
              <p>{t('roadmap_10_body')}</p>
              <span className="cps-roadmap-status dev"><span className="dot"/>{t('roadmap_status_dev')}</span>
            </div>
          </div>

          <p className="cps-roadmap-note">
            {t('roadmap_note')}
          </p>
          <InstallCTA/>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="cps-section cps-section-dark">
        <div className="cps-section-inner">
          <div className="cps-section-label">{t('faq_label')}</div>
          <h2 className="cps-section-title">{t('faq_title')}</h2>
          <div className="cps-faq">
            {/* La nona (2026-09-06) manda ai due articoli delle guide: e' una
                voce corta, il posto giusto per il seguito e' l'articolo. */}
            {[1,2,3,4,5,6,7,8,9].map(n => (
              <div key={n} className="cps-faq-item">
                <h4>{t(`faq_q${n}`)}</h4>
                <p>
                  {t(`faq_a${n}`)}
                  {n === 4 && (
                    <> <Trans i18nKey="home:faq_a4_seemore" components={{ 1: <a href="#security" style={{color:'var(--green)'}}/> }} /></>
                  )}
                  {n === 1 && (
                    <> <Trans i18nKey="home:faq_a1_seemore" components={{ 1: <a href="/terms" style={{color:'var(--green)'}} onClick={(e) => { e.preventDefault(); onTerms && onTerms(); }} /> }} /></>
                  )}
                  {n === 2 && (
                    <> <Trans i18nKey="home:faq_a2_seemore" components={{ 1: <a href="/terms" style={{color:'var(--green)'}} onClick={(e) => { e.preventDefault(); onTerms && onTerms(); }} /> }} /></>
                  )}
                </p>
                {n === 9 && (
                  <p className="cps-faq-guide">
                    <a href="/guida/mac" onClick={(e) => { e.preventDefault(); onApriGuida && onApriGuida('mac'); }}>
                      → {t('faq_a9_mac')}
                    </a>
                    <span aria-hidden="true"> · </span>
                    <a href="/guida/windows" onClick={(e) => { e.preventDefault(); onApriGuida && onApriGuida('windows'); }}>
                      {t('faq_a9_win')}
                    </a>
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="cps-section">
        <div className="cps-section-inner cps-contact">
          <div className="cps-section-label">{t('contact_label')}</div>
          <h2 className="cps-section-title">{t('contact_title')}</h2>
          <div className="cps-contact-cards">
            <div className="cps-contact-card">
              <span>📧</span>
              <h4>{t('contact_email_label')}</h4>
              <a href="mailto:info@lifetimecloud.me">info@lifetimecloud.me</a>
            </div>
            <div className="cps-contact-card">
              <span>🔐</span>
              <h4>{t('contact_security_label')}</h4>
              <a href="mailto:info@lifetimecloud.me">info@lifetimecloud.me</a>
            </div>
          </div>
        </div>
      </section>

      {/* Launch banner (footer) */}
      {isLaunch && (
        <div style={{background:'var(--verde-gradiente, linear-gradient(90deg,#16a34a,#22c55e))',color:'var(--su-verde, #000)',textAlign:'center',padding:'14px 16px',fontSize:14,fontWeight:700}}>
          🎉 {t('launch_banner_offer', { days: daysLeft })}
        </div>
      )}

      {/* FOOTER */}
      <footer className="cps-footer">
        <div className="cps-footer-inner">
          <div className="cps-footer-brand">
            <span>♾️ LifetimeCloud.me</span>
            <p>{t('footer_tagline')}</p>
          </div>
          <div className="cps-footer-links">
            <button onClick={() => scrollTo('how-it-works')}>{t('footer_link_how_it_works')}</button>
            <button onClick={() => scrollTo('plans')}>{t('footer_link_plans')}</button>
            <button onClick={() => scrollTo('security')}>{t('footer_link_security')}</button>
            <button onClick={() => scrollTo('faq')}>{t('footer_link_faq')}</button>
            <button onClick={onAbout}>{t('footer_link_about')}</button>
            <button onClick={onNews}>{t('footer_link_news')}</button>
            <button onClick={onLogin}>{t('footer_link_login')}</button>
            <button onClick={() => onRegister('piede')}>{t('footer_link_register')}</button>
            <button onClick={onTerms}>{t('common:footer_terms')}</button>
            <button onClick={onPrivacy}>{t('common:footer_privacy')}</button>
          </div>
        </div>
        <div className="cps-footer-bottom">
          <span>{t('footer_copyright')}</span>
          <span>🔒 {t('footer_badge')}</span>
        </div>
      </footer>

      {heroInAppWarningProps && <InAppBrowserWarning {...heroInAppWarningProps} />}
    </div>
  );
}
